import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  ProgressBar,
  Tooltip,
  OverlayTrigger,
} from 'react-bootstrap';
import PageHeader from '../../common/PageHeader';
import './Experience.css';

// Coding animation component
const TypingEffect = ({ text, speed = 100 }) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, speed);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex, text, speed]);

  return (
    <span className="typing-effect">
      {displayText}
      <span className="cursor">|</span>
    </span>
  );
};

// Animated skill bar component
const SkillBar = ({ skill, level, color, icon }) => (
  <div className="skill-item mb-3">
    <div className="d-flex justify-content-between align-items-center mb-2">
      <span className="skill-name">
        {icon && <i className={`${icon} me-2`} style={{color}}></i>}
        <strong>{skill}</strong>
      </span>
      <span className="skill-level">{level}%</span>
    </div>
    <div className="progress skill-progress" style={{height: '8px'}}>
      <div 
        className="progress-bar skill-bar-animated" 
        role="progressbar" 
        style={{
          width: `${level}%`,
          background: `linear-gradient(45deg, ${color}, ${color}99)`,
          animation: 'fillAnimation 2s ease-in-out'
        }}
      ></div>
    </div>
  </div>
);

// Technology card component with hover effects
const TechCard = ({ title, technologies, gradient, icon }) => (
  <Card className="tech-card border-0 shadow-lg h-100" style={{
    background: `linear-gradient(135deg, ${gradient})`,
    color: 'white',
    transform: 'translateY(0)',
    transition: 'all 0.3s ease'
  }}>
    <Card.Header className="border-0 bg-transparent">
      <div className="d-flex align-items-center">
        {icon && <i className={`${icon} me-3`} style={{fontSize: '2rem'}}></i>}
        <h5 className="mb-0 fw-bold">{title}</h5>
      </div>
    </Card.Header>
    <Card.Body>
      <div className="tech-tags">
        {technologies.map((tech, index) => (
          <Badge 
            key={index}
            className="me-2 mb-2 tech-badge"
            style={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              padding: '0.5rem 1rem',
              fontSize: '0.85rem',
              animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
            }}
          >
            {tech}
          </Badge>
        ))}
      </div>
    </Card.Body>
  </Card>
);

const Coding = () => {
  const [activeTab, setActiveTab] = useState('languages');

  const programmingLanguages = [
    { skill: 'Python', level: 95, color: '#3776AB', icon: 'fab fa-python' },
    { skill: 'JavaScript/TypeScript', level: 90, color: '#F7DF1E', icon: 'fab fa-js-square' },
    { skill: 'Java', level: 85, color: '#ED8B00', icon: 'fab fa-java' },
    { skill: 'Go', level: 75, color: '#00ADD8', icon: 'fas fa-bolt' },
    { skill: 'SQL', level: 90, color: '#336791', icon: 'fas fa-database' },
    { skill: 'Shell/Bash', level: 80, color: '#4EAA25', icon: 'fas fa-terminal' }
  ];

  const codeSnippets = [
    "# Building intelligent AI agents with LangChain",
    "from langchain.agents import initialize_agent, Tool",
    "// Orchestrating complex workflows with LangGraph", 
    "await agent.invoke({input: 'Analyze and process data'})"
  ];

  const [currentSnippet, setCurrentSnippet] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSnippet(prev => (prev + 1) % codeSnippets.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <PageHeader title="Coding Experience" />
      <div className="experience-section coding-page">
      <style>{`
        .coding-hero {
          background: linear-gradient(-45deg, #667eea, #764ba2, #6B73FF, #000DFF);
          background-size: 400% 400%;
          animation: gradientBG 15s ease infinite;
          color: white;
          min-height: 300px;
          display: flex;
          align-items: center;
        }

        @keyframes gradientBG {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .typing-effect {
          font-family: 'Courier New', monospace;
          font-size: 1.2rem;
          color: #00FF41;
          text-shadow: 0 0 10px #00FF41;
        }

        .cursor {
          animation: blink 1s infinite;
        }

        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }

        .tech-card:hover {
          transform: translateY(-10px) !important;
          box-shadow: 0 20px 40px rgba(0,0,0,0.3) !important;
        }

        .skill-progress {
          border-radius: 10px;
          overflow: hidden;
          background: rgba(255,255,255,0.2);
        }

        @keyframes fillAnimation {
          0% { width: 0%; }
          100% { width: var(--target-width); }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .code-window {
          background: #1e1e1e;
          border-radius: 10px;
          padding: 1rem;
          font-family: 'Courier New', monospace;
          color: #d4d4d4;
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
        }

        .code-header {
          display: flex;
          align-items: center;
          margin-bottom: 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid #333;
        }

        .code-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          margin-right: 8px;
        }

        .floating-icons {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          overflow: hidden;
        }

        .floating-icon {
          position: absolute;
          font-size: 2rem;
          opacity: 0.1;
          animation: float 6s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }

        .stats-card {
          background: linear-gradient(45deg, #667eea, #764ba2);
          color: white;
          border-radius: 15px;
          padding: 2rem;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .stats-card::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: linear-gradient(45deg, transparent, rgba(255,255,255,0.1), transparent);
          transform: rotate(45deg);
          animation: shine 3s infinite;
        }

        @keyframes shine {
          0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
          100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
        }

        .tab-button {
          background: transparent;
          border: 2px solid #667eea;
          color: #667eea;
          padding: 0.5rem 1.5rem;
          margin: 0.25rem;
          border-radius: 25px;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .tab-button.active, .tab-button:hover {
          background: #667eea;
          color: white;
          transform: scale(1.05);
        }
      `}</style>

      <div className="coding-hero position-relative">
        <div className="floating-icons">
          <i className="floating-icon fab fa-react" style={{top: '10%', left: '10%', animationDelay: '0s'}}></i>
          <i className="floating-icon fab fa-node-js" style={{top: '20%', right: '15%', animationDelay: '1s'}}></i>
          <i className="floating-icon fab fa-python" style={{bottom: '30%', left: '20%', animationDelay: '2s'}}></i>
          <i className="floating-icon fab fa-docker" style={{bottom: '15%', right: '25%', animationDelay: '3s'}}></i>
          <i className="floating-icon fas fa-database" style={{top: '40%', left: '50%', animationDelay: '4s'}}></i>
        </div>
        
        <Container>
          <Row className="align-items-center">
            <Col md={8}>
              <h1 className="display-4 fw-bold mb-4">
                <i className="fas fa-robot me-3"></i>
                AI-Powered Development
              </h1>
              <p className="lead mb-4">
                Specialized in building intelligent systems and AI agents using cutting-edge frameworks.
                Expert in LangChain, LangGraph, and modern AI development tools.
              </p>
              <div className="code-window">
                <div className="code-header">
                  <div className="code-dot" style={{backgroundColor: '#ff5f56'}}></div>
                  <div className="code-dot" style={{backgroundColor: '#ffbd2e'}}></div>
                  <div className="code-dot" style={{backgroundColor: '#27ca3f'}}></div>
                  <span className="ms-3 text-muted">~/coding-journey.js</span>
                </div>
                <TypingEffect text={codeSnippets[currentSnippet]} speed={80} />
              </div>
            </Col>
            <Col md={4}>
              <div className="stats-card">
                <h2 className="display-3 fw-bold">AI</h2>
                <p className="mb-2">Agent Frameworks</p>
                <h3 className="h4">LangChain</h3>
                <p className="mb-2">Expert Level</p>
                <h3 className="h4">LangGraph</h3>
                <p className="mb-0">Advanced Workflows</p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-5" style={{marginBottom: '2rem'}}>
        <Row className="mb-5">
          <Col md={12} className="text-center">
            <h2 className="display-5 fw-bold mb-4">Technical Expertise</h2>
            <div className="mb-4">
              <button 
                className={`tab-button ${activeTab === 'languages' ? 'active' : ''}`}
                onClick={() => setActiveTab('languages')}
              >
                Core Languages
              </button>
              <button 
                className={`tab-button ${activeTab === 'ai-frameworks' ? 'active' : ''}`}
                onClick={() => setActiveTab('ai-frameworks')}
              >
                AI Agent Frameworks
              </button>
              <button 
                className={`tab-button ${activeTab === 'tools' ? 'active' : ''}`}
                onClick={() => setActiveTab('tools')}
              >
                AI Tools & IDEs
              </button>
            </div>
          </Col>
        </Row>

        {activeTab === 'languages' && (
          <Row className="mb-5">
            <Col md={6}>
              <h3 className="mb-4">
                <i className="fas fa-code text-primary me-2"></i>
                Core Programming Languages
              </h3>
              {programmingLanguages.map((lang, index) => (
                <SkillBar 
                  key={index}
                  skill={lang.skill}
                  level={lang.level}
                  color={lang.color}
                  icon={lang.icon}
                />
              ))}
            </Col>
            <Col md={6}>
              <div className="code-window h-100">
                <div className="code-header">
                  <div className="code-dot" style={{backgroundColor: '#ff5f56'}}></div>
                  <div className="code-dot" style={{backgroundColor: '#ffbd2e'}}></div>
                  <div className="code-dot" style={{backgroundColor: '#27ca3f'}}></div>
                  <span className="ms-3 text-muted">ai-developer-stack.py</span>
                </div>
                <pre style={{color: '#d4d4d4', margin: 0}}>
{`# AI-First Development Stack
from langchain.agents import AgentExecutor
from langraph import StateGraph
import openai

class AIAgentBuilder:
    def __init__(self):
        self.frameworks = [
            "LangChain", "LangGraph", 
            "CrewAI", "AutoGen"
        ]
        self.languages = [
            "Python", "JavaScript", 
            "Java", "Go"
        ]
    
    def build_intelligent_system(self):
        return "AI-powered solutions"`}
                </pre>
              </div>
            </Col>
          </Row>
        )}

        {activeTab === 'ai-frameworks' && (
          <Row>
            <Col md={6} className="mb-4">
              <TechCard 
                title="LangChain Ecosystem"
                icon="fas fa-link"
                gradient="#FF6B6B, #4ECDC4"
                technologies={['LangChain Core', 'LangChain Community', 'LangSmith', 'LangServe', 'RAG Pipelines', 'Vector Stores']}
              />
            </Col>
            <Col md={6} className="mb-4">
              <TechCard 
                title="LangGraph & Orchestration"
                icon="fas fa-project-diagram"
                gradient="#667eea, #764ba2"
                technologies={['LangGraph', 'Complex Workflows', 'State Management', 'Multi-Agent Systems', 'Decision Trees', 'Conditional Logic']}
              />
            </Col>
            <Col md={6} className="mb-4">
              <TechCard 
                title="AI Agent Frameworks"
                icon="fas fa-robot"
                gradient="#8E2DE2, #4A00E0"
                technologies={['CrewAI', 'AutoGen', 'AgentGPT', 'SuperAGI', 'Multi-Agent Orchestration', 'Tool Integration']}
              />
            </Col>
            <Col md={6} className="mb-4">
              <TechCard 
                title="LLM Integration"
                icon="fas fa-brain"
                gradient="#11998e, #38ef7d"
                technologies={['OpenAI API', 'Anthropic Claude', 'Hugging Face', 'Local LLMs', 'Fine-tuning', 'Prompt Engineering']}
              />
            </Col>
          </Row>
        )}

        {activeTab === 'tools' && (
          <Row>
            <Col md={3} className="mb-4">
              <TechCard 
                title="AI Development IDEs"
                icon="fas fa-code"
                gradient="#FF9500, #FF5722"
                technologies={['VS Code', 'Cursor', 'PyCharm', 'Jupyter Lab', 'Google Colab', 'Replit']}
              />
            </Col>
            <Col md={3} className="mb-4">
              <TechCard 
                title="AI Coding Assistants"
                icon="fas fa-magic"
                gradient="#4A90E2, #7B68EE"
                technologies={['GitHub Copilot', 'Cursor AI', 'Tabnine', 'CodeWhisperer', 'Codeium', 'Sourcegraph Cody']}
              />
            </Col>
            <Col md={3} className="mb-4">
              <TechCard 
                title="Vector & Data Tools"
                icon="fas fa-database"
                gradient="#F7931E, #FFB347"
                technologies={['Pinecone', 'Weaviate', 'Chroma', 'FAISS', 'Elasticsearch', 'pgvector']}
              />
            </Col>
            <Col md={3} className="mb-4">
              <TechCard 
                title="AI Ops & Deployment"
                icon="fas fa-cloud"
                gradient="#667eea, #764ba2"
                technologies={['MLflow', 'Weights & Biases', 'LangSmith', 'Streamlit', 'Gradio', 'Docker']}
              />
            </Col>
          </Row>
        )}

        <Row className="mt-5">
          <Col md={12}>
            <Card className="border-0 shadow-lg">
              <Card.Header className="bg-gradient text-white text-center" style={{background: 'linear-gradient(45deg, #667eea, #764ba2)'}}>
                <h4 className="mb-0">
                  <i className="fas fa-robot me-2"></i>
                  AI Development Expertise & Philosophy
                </h4>
              </Card.Header>
              <Card.Body className="p-4">
                <Row>
                  <Col md={6}>
                    <h5 className="text-primary mb-3">AI Agent Development</h5>
                    <ul className="list-unstyled">
                      <li className="mb-2">
                        <i className="fas fa-check-circle text-success me-2"></i>
                        <strong>LangChain Mastery:</strong> Building complex RAG systems and agent workflows
                      </li>
                      <li className="mb-2">
                        <i className="fas fa-check-circle text-success me-2"></i>
                        <strong>LangGraph Expertise:</strong> Orchestrating multi-step AI processes with state management
                      </li>
                      <li className="mb-2">
                        <i className="fas fa-check-circle text-success me-2"></i>
                        <strong>Multi-Agent Systems:</strong> Coordinating teams of AI agents for complex tasks
                      </li>
                      <li className="mb-2">
                        <i className="fas fa-check-circle text-success me-2"></i>
                        <strong>Tool Integration:</strong> Connecting AI agents with external APIs and services
                      </li>
                    </ul>
                  </Col>
                  <Col md={6}>
                    <h5 className="text-primary mb-3">AI-Enhanced Development</h5>
                    <ul className="list-unstyled">
                      <li className="mb-2">
                        <i className="fas fa-star text-warning me-2"></i>
                        Expert in AI-powered IDEs and coding assistants
                      </li>
                      <li className="mb-2">
                        <i className="fas fa-star text-warning me-2"></i>
                        Advanced prompt engineering and LLM optimization
                      </li>
                      <li className="mb-2">
                        <i className="fas fa-star text-warning me-2"></i>
                        Vector database design and similarity search systems
                      </li>
                      <li className="mb-2">
                        <i className="fas fa-star text-warning me-2"></i>
                        Building production-ready AI applications and APIs
                      </li>
                    </ul>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
    </>
  );
};

export default Coding;